/**
 * The item-label layout solver.
 *
 * A dropped item's label floats over its position in the world, so when a pack dies on
 * one spot — which is what a pack dying looks like — a dozen labels land on top of each
 * other and the pile is unreadable. Worse than unreadable: overlapping labels are also
 * overlapping *click targets*, so the item a player thinks they are clicking is not
 * necessarily the one on top. This file is the "vertical offset algorithm" the spec's
 * layout engine section asks for.
 *
 * Pure geometry. No DOM, no measurement, no clock, no randomness — the caller measures
 * its labels (or computes a width from the font size the filter chose) and hands the
 * boxes in. That is what makes the degenerate cases testable: forty labels at one point
 * is two lines of a test here and an act of God in a browser.
 *
 * ## The rules, and what each one prevents
 *
 * - **Sorted by priority, then position, then id.** A total order over the input, so
 *   the same drops laid out twice give byte-identical output regardless of the order
 *   the snapshot happened to list them in. Without it a label column would reshuffle
 *   every tick as the server's iteration order changed, which reads as flicker.
 * - **The highest-priority label keeps its exact anchor.** Everything else moves around
 *   it. If the important label moved, the player would have to re-find it after every
 *   drop.
 * - **Displacement is upward first.** A label hangs above its item; pushing the
 *   collisions *down* would walk them over the drops themselves and over whatever is
 *   still fighting the player, and it would put a label below the item it belongs to,
 *   which reads as belonging to a different one. Downward is the fallback when there is
 *   no room above (**chosen** — the spec says "vertical offset" and does not say which
 *   way).
 * - **A label that cannot be placed without overlapping is hidden, not squeezed in.**
 *   Two overlapping labels are worse than one label and a count, because neither can be
 *   read or reliably clicked.
 * - **`maxLabels` caps the pile.** Above roughly a screenful there is nothing to
 *   salvage by solving harder; the caller shows "+7 more" from `overflow`.
 */

import type { FilterTier } from './parse';

/** One label wanting a home, in CSS pixels of screen space. */
export interface LabelAnchor {
  /** Loot id. Carried through to the placement so the caller can key its DOM by it. */
  id: string;
  /** Screen x of the item's anchor point. The label is centred on it. */
  x: number;
  /** Screen y of the item's anchor point. The label's *bottom* sits here. */
  y: number;
  width: number;
  height: number;
  /**
   * Priority tier, 1 (godly) to 4 (trash). Lower keeps its place and pushes the others
   * around. Defaults to 4, so an unprioritised label yields to everything.
   */
  tier?: FilterTier;
  /**
   * False for an anchor the projector could not see — behind the camera, or off the
   * near plane. Hidden without being laid out. Defaults to true.
   */
  visible?: boolean;
}

/** Where a label ended up. */
export interface PlacedLabel {
  id: string;
  /** Left edge, after centring on the anchor and clamping to the viewport. */
  left: number;
  /** Top edge, after displacement. */
  top: number;
  width: number;
  height: number;
  /**
   * How far the label was pushed from where it wanted to be: negative is upward.
   * Exposed so a caller can draw a leader line back to the item when it is large.
   */
  offsetY: number;
  tier: FilterTier;
}

export interface LabelLayout {
  /** Placed labels, in priority order. Guaranteed pairwise non-overlapping. */
  placed: PlacedLabel[];
  /** Ids that could not be shown: off-screen, over the cap, or with nowhere to go. */
  hidden: string[];
  /** `hidden.length`, named for the "+N more" the HUD shows. */
  overflow: number;
}

export interface LayoutOptions {
  /** The area labels must stay inside. Defaults to a 1920×1080 screen. */
  viewport?: { width: number; height: number };
  /** Vertical space left between two stacked labels. */
  gap?: number;
  /** How close to a viewport edge a label may sit. */
  margin?: number;
  /** Most labels to show at once. The rest become `overflow`. */
  maxLabels?: number;
}

/** Gap between stacked labels. Two pixels reads as a rendering artefact; four reads as a list. */
export const DEFAULT_GAP = 4;

/** Keep-out from the viewport edge, matching `ui/tooltip.ts`'s margin. */
export const DEFAULT_MARGIN = 8;

/**
 * Default cap on visible labels.
 *
 * **Chosen.** The spec asks for stacking and says nothing about a limit, but a boss
 * dying can drop more items than a screen has room for, and at that point the honest
 * output is a readable dozen plus a count. Twelve is roughly a third of a 1080p screen
 * at the default label height, which leaves the fight visible behind the loot.
 */
export const DEFAULT_MAX_LABELS = 12;

const DEFAULT_VIEWPORT = { width: 1920, height: 1080 };

/** Lowest priority, used for an anchor that did not state one. */
const LOWEST_TIER: FilterTier = 4;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Whether two boxes share any area.
 *
 * Touching edges do not count as overlapping — two labels exactly `gap` apart are
 * adjacent, not colliding, and treating that as a collision would make the solver push
 * every label one extra step forever.
 */
export function boxesOverlap(a: Box, b: Box): boolean {
  return (
    a.left < b.left + b.width &&
    b.left < a.left + a.width &&
    a.top < b.top + b.height &&
    b.top < a.top + a.height
  );
}

/**
 * A coordinate that can be compared.
 *
 * A projection behind the camera yields NaN, and every comparison against NaN is
 * false — which makes `Array.sort`'s result implementation-defined for the *whole*
 * array, not just the bad element. One unprojectable drop must not be able to reorder
 * the labels of the good ones, so a non-finite coordinate sorts last instead.
 */
function sortable(n: number): number {
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

/** Total order: priority, then screen position, then id. See the header. */
function byPriority(a: LabelAnchor, b: LabelAnchor): number {
  const tierA = a.tier ?? LOWEST_TIER;
  const tierB = b.tier ?? LOWEST_TIER;
  if (tierA !== tierB) return tierA - tierB;
  if (sortable(a.y) !== sortable(b.y)) return sortable(a.y) - sortable(b.y);
  if (sortable(a.x) !== sortable(b.x)) return sortable(a.x) - sortable(b.x);
  return a.id.localeCompare(b.id);
}

/**
 * Slides a box vertically until it clears everything already placed.
 *
 * `direction` is -1 for up, +1 for down. Returns null when it would leave the allowed
 * band. The loop is bounded by the number of placed boxes because each pass either
 * clears everything or moves past at least one box it has not passed yet.
 */
function slide(
  box: Box,
  placed: readonly Box[],
  direction: -1 | 1,
  gap: number,
  minTop: number,
  maxBottom: number,
): number | null {
  let top = box.top;

  for (let guard = 0; guard <= placed.length; guard += 1) {
    let collided = false;
    let next = top;

    for (const other of placed) {
      if (!boxesOverlap({ ...box, top }, other)) continue;
      collided = true;
      const candidate =
        direction === -1
          ? other.top - box.height - gap // just above it
          : other.top + other.height + gap; // just below it
      // The furthest candidate in the direction of travel, not the last one found:
      // clearing the nearest collision alone can drop the box straight back into one
      // it had already passed, and the loop would then oscillate between two boxes
      // instead of terminating. Taking the extreme makes each pass strictly monotonic.
      next = direction === -1 ? Math.min(next, candidate) : Math.max(next, candidate);
    }

    if (!collided) {
      if (top < minTop || top + box.height > maxBottom) return null;
      return top;
    }

    top = next;
    // Out of the band already: no later pass moves back toward it, so stop.
    if (top < minTop || top + box.height > maxBottom) return null;
  }

  return null;
}

/**
 * Lays out floating item labels so none of them overlap.
 *
 * Deterministic: the same anchors in any order produce the same result, which is what
 * lets a caller run this every frame without the column jittering.
 */
export function layoutLabels(
  anchors: readonly LabelAnchor[],
  options: LayoutOptions = {},
): LabelLayout {
  const viewport = options.viewport ?? DEFAULT_VIEWPORT;
  const gap = options.gap ?? DEFAULT_GAP;
  const margin = options.margin ?? DEFAULT_MARGIN;
  const maxLabels = options.maxLabels ?? DEFAULT_MAX_LABELS;

  const minTop = margin;
  const maxBottom = viewport.height - margin;
  const minLeft = margin;

  const placed: PlacedLabel[] = [];
  const hidden: string[] = [];

  // A copy: mutating the caller's array would be a surprise, and this runs over a
  // signal's value in the HUD.
  const ordered = [...anchors].sort(byPriority);

  for (const anchor of ordered) {
    if (anchor.visible === false || !isPlaceable(anchor, viewport, margin)) {
      hidden.push(anchor.id);
      continue;
    }

    if (placed.length >= maxLabels) {
      hidden.push(anchor.id);
      continue;
    }

    // Centred on the anchor, clamped so a drop at the screen edge still shows its
    // whole label rather than half of one.
    const maxLeft = Math.max(minLeft, viewport.width - margin - anchor.width);
    const left = Math.min(Math.max(anchor.x - anchor.width / 2, minLeft), maxLeft);
    // Clamped into the band before any collision work: a drop just under the top edge
    // of the screen wants a label partly off it, and pinning that label to the margin
    // is right where refusing to place it at all would be a drop with no label for no
    // reason the player can see. `offsetY` is measured from the *unclamped* position,
    // so it reports the clamp as displacement too — it is the distance between the
    // label and the item it names, which is what a leader line would have to cover.
    const wantedTop = anchor.y - anchor.height;
    const desiredTop = Math.min(
      Math.max(wantedTop, minTop),
      Math.max(minTop, maxBottom - anchor.height),
    );
    const box: Box = { left, top: desiredTop, width: anchor.width, height: anchor.height };

    // Up first, down as a fallback: see the header. The first label placed never moves,
    // because there is nothing yet to collide with.
    const top =
      slide(box, placed, -1, gap, minTop, maxBottom) ??
      slide(box, placed, 1, gap, minTop, maxBottom);

    if (top === null) {
      hidden.push(anchor.id);
      continue;
    }

    placed.push({
      id: anchor.id,
      left,
      top,
      width: anchor.width,
      height: anchor.height,
      offsetY: top - wantedTop,
      tier: anchor.tier ?? LOWEST_TIER,
    });
  }

  return { placed, hidden, overflow: hidden.length };
}

/**
 * Whether an anchor is worth laying out at all.
 *
 * An anchor point outside the viewport belongs to an item off the side of the screen;
 * clamping it would pin a label to the edge for something the player cannot see, and a
 * row of edge-pinned labels for items in the next room is noise. Non-finite coordinates
 * come from a projection behind the camera and would poison every comparison.
 */
function isPlaceable(
  anchor: LabelAnchor,
  viewport: { width: number; height: number },
  margin: number,
): boolean {
  if (!Number.isFinite(anchor.x) || !Number.isFinite(anchor.y)) return false;
  if (anchor.width <= 0 || anchor.height <= 0) return false;
  return (
    anchor.x >= -margin &&
    anchor.x <= viewport.width + margin &&
    anchor.y >= -margin &&
    anchor.y <= viewport.height + margin
  );
}
